import { Alarm, AlarmAlertGroup, AlarmMachine } from '../../gql/generated';
import type { AlarmFilters } from '../../hooks/useAlarmFilters';

/**
 * One row of the Pulse alerts table.
 *
 * A multi-signal alert is stored as one alarm per signal plus an alert group
 * over them. The table shows that group as a single row that expands into its
 * member rows, so the list counts a grouped alert once.
 */
export interface AlarmTableRow {
  /** The alarm id, or the group id on a group row. */
  id: string;
  /** The alert this row stands for. A group row carries its first member. */
  alarm: Alarm;
  /**
   * Set on a group row. Every alert Pulse saves is a group, so a group of one
   * signal carries it too, without members.
   */
  group?: AlarmAlertGroup;
  /** Member rows of a multi-signal group row, one per signal. */
  members?: AlarmTableRow[];
  /** Name of the group a member row belongs to. */
  groupName?: string;
  /** Flash the row after it was just created. Read by the data table. */
  __highlighted?: boolean;
}

/** True while the row stands for an alert group instead of a single alert. */
export function isAlarmGroupRow(row: AlarmTableRow): boolean {
  return Boolean(row.group);
}

/**
 * Whether the signed-in user may edit or switch this alert.
 *
 * Only the user who created an alert edits it or switches it on and off
 * (bug 166139). Delete is not gated here: anyone may remove an alert, so an
 * alert whose owner is away does not stay behind. EDMS stamps its own user id
 * on every alarm, so ownership is that id against the signed-in user's EDMS
 * id. With "show all users" off the list only holds the user's own alerts,
 * and without a known EDMS id nothing can be told apart, so both leave every
 * row editable rather than lock the user out of their own alerts.
 */
export function canEditAlarmRow(
  row: AlarmTableRow,
  options: { showAllUsers: boolean; edmsUserId: string | null | undefined }
): boolean {
  if (!options.showAllUsers || !options.edmsUserId) return true;
  return row.alarm.userId === options.edmsUserId;
}

/**
 * Fold the alarms that belong to an alert group into one row per group.
 *
 * A group row takes the place of its first member, so alarms sorted by name
 * keep their order: every member name starts with the group name.
 */
export function buildAlarmRows(alarms: Alarm[]): AlarmTableRow[] {
  const rows: AlarmTableRow[] = [];
  const groupRows = new Map<string, AlarmTableRow>();

  for (const alarm of alarms) {
    // An alarm can sit in several groups. It belongs under the first one, the
    // same one the edit wizard opens. Pulse itself never puts an alarm in more
    // than one group; only a direct EDMS edit can, and that case is accepted.
    const group = alarm.alertGroups?.[0];

    if (!group) {
      rows.push({ id: alarm.id, alarm });
      continue;
    }

    const memberRow: AlarmTableRow = {
      id: alarm.id,
      alarm,
      groupName: group.name,
    };
    const existing = groupRows.get(group.id);

    if (existing) {
      existing.members!.push(memberRow);
      continue;
    }

    const groupRow: AlarmTableRow = {
      id: group.id,
      alarm,
      group,
      members: [memberRow],
    };
    groupRows.set(group.id, groupRow);
    rows.push(groupRow);
  }

  // A group of one signal reads like a single alert: no expander, no nested
  // member row. It keeps its group, because EDMS evaluates and notifies alert
  // groups only, so the group is where its actions and channels belong.
  for (const groupRow of groupRows.values()) {
    if (groupRow.members?.length === 1) {
      groupRow.members = undefined;
    }
  }

  return rows;
}

/** One alarm against the filters, the way the EDMS-side filter reads them. */
function alarmMatchesFilters(alarm: Alarm, filters: AlarmFilters): boolean {
  if (filters.name.trim()) {
    const search = filters.name.toLowerCase();
    if (!alarm.name.toLowerCase().includes(search)) return false;
  }

  if (filters.machines.length) {
    const onMachine = (alarm.machines || []).some((machine) =>
      filters.machines.includes(machine.machineId || '')
    );
    if (!onMachine) return false;
  }

  return filters.signals.length === 0 || filters.signals.includes(alarm.signal);
}

/**
 * The rows the filters keep. A group row stays whole while any member matches,
 * so its actions and its panel always cover every signal of the alert — a
 * filter narrows what the table lists, never what a group row stands for.
 */
export function filterAlarmRows(
  rows: AlarmTableRow[],
  filters: AlarmFilters
): AlarmTableRow[] {
  return rows.filter((row) =>
    alarmRowAlarms(row).some((alarm) => alarmMatchesFilters(alarm, filters))
  );
}

/** Every row a group row stands for, or the row itself for a single alert. */
export function alarmRowAlarms(row: AlarmTableRow): Alarm[] {
  return row.members ? row.members.map((member) => member.alarm) : [row.alarm];
}

/** The machines a row covers. A group row covers the union of its members'. */
export function alarmRowMachines(row: AlarmTableRow): AlarmMachine[] {
  if (!row.members) return row.alarm.machines || [];

  const byId = new Map<string, AlarmMachine>();
  for (const alarm of alarmRowAlarms(row)) {
    for (const machine of alarm.machines || []) {
      byId.set(machine.machineId || machine.id, machine);
    }
  }
  return [...byId.values()];
}

/**
 * What the Alert name column shows. A member row drops the group name its
 * stored name starts with, so the group name reads once per group.
 */
export function alarmRowName(row: AlarmTableRow): string {
  if (row.group) return row.group.name;

  const prefix = row.groupName ? `${row.groupName} - ` : '';
  return prefix && row.alarm.name.startsWith(prefix)
    ? row.alarm.name.slice(prefix.length)
    : row.alarm.name;
}

/**
 * The group row one alarm sits under. A signal of a grouped alert opens the
 * whole alert, so the panel needs the group row that holds the signal.
 */
export function findAlarmGroupRow(
  rows: AlarmTableRow[],
  alarmId: string
): AlarmTableRow | undefined {
  return rows.find(
    (row) =>
      Boolean(row.group) &&
      (row.alarm.id === alarmId ||
        (row.members?.some((member) => member.id === alarmId) ?? false))
  );
}

/** True when the id names a row of the list, or a signal inside a group row. */
export function hasAlarmRow(rows: AlarmTableRow[], id: string): boolean {
  return rows.some(
    (row) =>
      row.id === id ||
      row.alarm.id === id ||
      (row.members?.some((member) => member.id === id) ?? false)
  );
}
